'use client';

/**
 * SidebarRail Component (70px)
 *
 * Fixed vertical icon strip on the left. Shows one icon per standalone item
 * and one icon per section. Clicking a section expands/collapses the SidebarPanel.
 *
 * @see PR #856 — Navigation & Layout Redesign
 */

import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssessmentIcon from '@mui/icons-material/Assessment';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BarChartIcon from '@mui/icons-material/BarChart';
import BoltIcon from '@mui/icons-material/Bolt';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import DarkModeIcon from '@mui/icons-material/Brightness4';
import LightModeIcon from '@mui/icons-material/Brightness7';
import CalculateIcon from '@mui/icons-material/Calculate';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CodeIcon from '@mui/icons-material/Code';
import ContactsIcon from '@mui/icons-material/Contacts';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DashboardIcon from '@mui/icons-material/Dashboard';
import DescriptionIcon from '@mui/icons-material/Description';
import DomainIcon from '@mui/icons-material/Domain';
import DrawIcon from '@mui/icons-material/Draw';
import EmailIcon from '@mui/icons-material/Email';
import FolderIcon from '@mui/icons-material/Folder';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import GroupsIcon from '@mui/icons-material/Groups';
import HandshakeIcon from '@mui/icons-material/Handshake';
import HistoryEduIcon from '@mui/icons-material/HistoryEdu';
import HomeIcon from '@mui/icons-material/Home';
import InboxIcon from '@mui/icons-material/Inbox';
import InfoIcon from '@mui/icons-material/Info';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import LogoutIcon from '@mui/icons-material/Logout';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PaymentIcon from '@mui/icons-material/Payment';
import PushPinIcon from '@mui/icons-material/PushPin';
import RadarIcon from '@mui/icons-material/Radar';
import ScoreboardIcon from '@mui/icons-material/Scoreboard';
import SendIcon from '@mui/icons-material/Send';
import SettingsIcon from '@mui/icons-material/Settings';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import StorageIcon from '@mui/icons-material/Storage';
import StorefrontIcon from '@mui/icons-material/Storefront';
import TimelineIcon from '@mui/icons-material/Timeline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WidgetsIcon from '@mui/icons-material/Widgets';
import WorkspacesIcon from '@mui/icons-material/Workspaces';
import {
  Avatar,
  Box,
  ButtonBase,
  Divider,
  Drawer,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import type { NavItem, NavSection, SidebarConfig, VisibilityContext } from '@ops-matrix/shared-types';
import { checkVisibility, filterNavSections } from '@ops-matrix/shared-types';
import type React from 'react';
import { memo, useMemo, useState } from 'react';
import { useI18n } from '../../hooks/useI18n';
import { RAIL_EXPANDED_WIDTH, RAIL_WIDTH } from '../../hooks/useSidebarRail';
import { iconSizes } from '../../theme/typography';
import { computeUserInitials } from '../../utils/user-initials';
import { HEADER_HEIGHT } from '../AppNavBar/AppNavBar';
import { GlassTooltip } from '../GlassTooltip';

// ============================================================================
// Icon Mapping (subset for rail — uses first item icon or section icon)
// ============================================================================

const RAIL_ICON_MAP: Record<string, React.ReactElement> = {
  Home: <HomeIcon />,
  Dashboard: <DashboardIcon />,
  Inbox: <InboxIcon />,
  Assignment: <AssignmentIcon />,
  Contacts: <ContactsIcon />,
  AccountTree: <AccountTreeIcon />,
  FolderShared: <FolderSharedIcon />,
  Handshake: <HandshakeIcon />,
  Payment: <PaymentIcon />,
  Assessment: <AssessmentIcon />,
  AccountBalance: <AccountBalanceIcon />,
  Storefront: <StorefrontIcon />,
  Calculate: <CalculateIcon />,
  Groups: <GroupsIcon />,
  AutoAwesome: <AutoAwesomeIcon />,
  TrendingUp: <TrendingUpIcon />,
  Radar: <RadarIcon />,
  Notifications: <NotificationsIcon />,
  BarChart: <BarChartIcon />,
  Workspaces: <WorkspacesIcon />,
  Settings: <SettingsIcon />,
  SettingsInputComponent: <SettingsInputComponentIcon />,
  Info: <InfoIcon />,
  // Signature / documents-to-sign / scorecard / pricing / dev / storage
  Draw: <DrawIcon />,
  HistoryEdu: <HistoryEduIcon />,
  Scoreboard: <ScoreboardIcon />,
  LocalOffer: <LocalOfferIcon />,
  Code: <CodeIcon />,
  Storage: <StorageIcon />,
  Bookmark: <BookmarkIcon />,
  Checklist: <ChecklistIcon />,
  // Admin portal icons
  Domain: <DomainIcon />,
  Timeline: <TimelineIcon />,
  CreditCard: <CreditCardIcon />,
  MonitorHeart: <MonitorHeartIcon />,
  Widgets: <WidgetsIcon />,
  Bolt: <BoltIcon />,
  // Consumer portal icons
  Description: <DescriptionIcon />,
  Folder: <FolderIcon />,
  Email: <EmailIcon />,
  Send: <SendIcon />,
};

function getRailIcon(iconName: string): React.ReactElement {
  return RAIL_ICON_MAP[iconName] || <DashboardIcon />;
}

// ============================================================================
// Types
// ============================================================================

export interface SidebarRailUser {
  name?: string;
  email?: string;
  avatarUrl?: string;
}

export interface SidebarRailProps {
  /** Sidebar configuration */
  config: SidebarConfig;
  /** Current pathname for active state detection */
  pathname: string;
  /** Handler when a standalone item is clicked */
  onNavigate: (path: string) => void;
  /**
   * Optional hover prefetch hook. Portals can wire this to `router.prefetch`
   * (Next.js) to warm the next route's JS + data before the click lands.
   * Called with the destination path on mouseenter.
   */
  onHoverPath?: (path: string) => void;
  /** Handler when a section rail icon is clicked */
  onSectionClick: (sectionId: string) => void;
  /**
   * Handler when a section rail icon is hovered. Used by the hover-preview
   * pattern in `hoverExpand` mode: hovering an icon previews that section
   * in the panel without committing. Mouse-out of the rail+panel reverts
   * to the active page's section (handled by `useSidebarRail`).
   * Pass `null` from `onMouseLeave` on individual icons is NOT needed —
   * the parent's outer onMouseLeave clears it.
   */
  onSectionHover?: (sectionId: string) => void;
  /** Currently expanded section in the panel */
  activeSection: string | null;
  /** Visibility context for filtering items */
  visibilityContext?: VisibilityContext;
  /** Handler for settings gear click */
  onSettingsClick?: () => void;
  /** Whether the rail is fully hidden (Asana-style full collapse) */
  hidden?: boolean;
  /** Current user for avatar at bottom of rail */
  user?: SidebarRailUser;
  /** Handler for logout */
  onLogout?: () => void;
  /** Handler for profile navigation */
  onProfile?: () => void;
  /** i18n key for the settings menu item label (default: 'navigation.settings') */
  settingsMenuLabel?: string;
  /** Whether dark mode is active (for theme toggle in avatar menu) */
  isDarkMode?: boolean;
  /** Handler for theme toggle */
  onThemeToggle?: () => void;
  /**
   * When true, the rail can be expanded to its labelled width via the
   * pin button rendered in its header. Default `false` keeps the legacy
   * column layout (icon stacked over a tiny label).
   *
   * Unlike the previous hover-overlay implementation, expansion now
   * *pushes* page content to the right — `currentSidebarWidth` in
   * `PortalLayout` follows the rail width so the content area inset
   * grows when expanded. The previous `position: fixed` overlay was
   * removed because the user wanted predictable layout (mouse
   * proximity should not reflow the page).
   */
  expandable?: boolean;
  /**
   * Controlled expanded state. When `expandable` is true, the parent
   * (PortalLayout via useSidebarRail) owns this and toggles via
   * `onPinToggle`. The pin button inside the rail header dispatches
   * the toggle.
   */
  expanded?: boolean;
  /** Pin/unpin callback wired to `useSidebarRail.toggleRailExpanded`. */
  onPinToggle?: () => void;
  /**
   * When true, the rail paints its own right divider + rounded top-right
   * corner. Use this when the secondary `<SidebarPanel>` is closed so
   * the rail still presents the same right-edge chrome the panel was
   * providing while open. When the panel is open, leave this `false` —
   * the panel's left-edge chrome takes over and a rail-side rounded
   * corner would notch against the panel's rounded top-left.
   */
  showRightEdge?: boolean;
}

// ============================================================================
// Helpers
// ============================================================================

/**
 * Check if any item in a section matches the current pathname.
 */
function isSectionActive(section: NavSection, pathname: string): boolean {
  return section.items.some(
    (item) =>
      pathname === item.path ||
      pathname.startsWith(`${item.path}/`) ||
      item.tabs?.some((tab) => pathname === tab.path || pathname.startsWith(`${tab.path}/`)),
  );
}

/**
 * Check if a standalone item is active.
 */
function isItemActive(item: NavItem, pathname: string): boolean {
  return pathname === item.path || pathname.startsWith(`${item.path}/`);
}

// ============================================================================
// Component
// ============================================================================

const SidebarRailImpl: React.FC<SidebarRailProps> = ({
  config,
  pathname,
  onNavigate,
  onHoverPath,
  onSectionClick,
  onSectionHover,
  activeSection,
  visibilityContext = {},
  onSettingsClick,
  hidden = false,
  user,
  onLogout,
  onProfile,
  settingsMenuLabel,
  isDarkMode,
  onThemeToggle,
  expandable = false,
  expanded: controlledExpanded = false,
  onPinToggle,
  showRightEdge = false,
}) => {
  const theme = useTheme();
  const { t } = useI18n();
  const [avatarMenuAnchor, setAvatarMenuAnchor] = useState<null | HTMLElement>(null);
  // Expansion is now controlled by the parent (PortalLayout via the
  // pin button). Hover-driven expansion was removed — the user
  // reported that auto-expanding on mouse proximity made layout
  // unpredictable; pinning is explicit and pushes page content.
  const expanded = expandable && controlledExpanded;
  // Rail rests at the standard RAIL_WIDTH so the AppNavBar hamburger
  // (also in a RAIL_WIDTH-wide column) stays vertically aligned with
  // the rail icons. When pinned open, the rail widens to
  // RAIL_EXPANDED_WIDTH; the page content area shifts right because
  // PortalLayout sums currentSidebarWidth from `railWidth`.
  const restWidth = RAIL_WIDTH;
  const railWidth = expanded ? RAIL_EXPANDED_WIDTH : restWidth;

  // Filter sections by visibility and hideFromRail flag
  const visibleSections = useMemo(
    () => filterNavSections(config.sections, visibilityContext).filter((s) => !s.hideFromRail),
    [config.sections, visibilityContext],
  );

  // Check if active section is hidden from rail (e.g., settings/control-center).
  // When true, the avatar circle is highlighted instead of a rail icon.
  const isSettingsActive = useMemo(
    () => !!activeSection && config.sections.some((s) => s.id === activeSection && s.hideFromRail),
    [activeSection, config.sections],
  );

  // Filter standalone items by visibility
  const visibleStandaloneItems = useMemo(
    () =>
      (config.standaloneItems || []).filter(
        (item) => !item.visibility || checkVisibility(item.visibility, visibilityContext),
      ),
    [config.standaloneItems, visibilityContext],
  );

  const isDark = theme.palette.mode === 'dark';

  const railContent = (
    <Box
      sx={{
        width: railWidth,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: expanded ? 'stretch' : 'center',
        bgcolor: isDark ? 'rgba(18, 18, 20, 0.92)' : 'rgba(245, 245, 248, 0.95)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        // Asana-style curved top-right + bottom-right corners on the
        // rail itself so the rail surface reads as a self-contained
        // panel even when the secondary SidebarPanel is closed. When
        // the panel is open it covers these edges with its own
        // top-left curve and the visual continues to read as one
        // unbroken glass surface.
        borderRadius: `0 ${theme.shape.borderRadius}px ${theme.shape.borderRadius}px 0`,
        pt: `${HEADER_HEIGHT + 12}px`,
        pb: 1,
        // Gap stays constant across rest/expanded states so cumulative
        // row positions don't shift when the user moves their cursor
        // over the rail. 0.75 (6px) gives the menu visible breathing
        // room between rows — the prior 0.25 (2px) was visually too
        // packed in designer review (rows ran into each other and the
        // expanded mode read as a wall of equal-weight options).
        gap: 0.75,
        overflowY: 'auto',
        overflowX: 'hidden',
        '&::-webkit-scrollbar': { width: 0 },
        // Smooth width transition on the rail container. Items inside
        // also transition (icon-only ↔ icon+label) to keep the
        // expansion feeling like one continuous animation.
        transition: theme.transitions.create(['width'], {
          duration: theme.transitions.duration.shorter,
          easing: theme.transitions.easing.easeOut,
        }),
      }}
    >
      {/* Rail pin button — only visible while the rail is expanded.
          When collapsed (icon-only strip) the pin is hidden so the
          rest state stays a clean column of icons; expansion is
          triggered by the AppNavBar hamburger and by section clicks
          that pop the secondary panel. The user wanted a quiet rest
          state with no extra chrome. */}
      {expandable && onPinToggle && expanded && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', px: 0.5 }}>
          <GlassTooltip title={t('navigation.unpinSidebar', 'Collapse sidebar')} placement="top">
            <ButtonBase
              onClick={onPinToggle}
              disableRipple
              aria-label={t('navigation.unpinSidebar', 'Collapse sidebar')}
              aria-pressed={true}
              sx={{
                // 44×44 minimum touch target (Apple HIG / MD3 floor).
                // The PushPin icon is visually smaller; the extra area is
                // transparent but tappable on touch devices. Center via
                // inline-flex so the icon sits in the middle of the box.
                width: 44,
                height: 44,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                color: 'primary.main',
                '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)' },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 1,
                },
                transition: 'color 0.15s ease',
              }}
            >
              <PushPinIcon sx={{ fontSize: iconSizes.sm }} />
            </ButtonBase>
          </GlassTooltip>
        </Box>
      )}

      {/* Standalone Items (e.g., Dashboard/Home) */}
      {visibleStandaloneItems.map((item) => {
        const active = isItemActive(item, pathname);
        const itemLabel = t(item.label) || item.label;
        // Skip the tooltip when the rail is expanded — the inline label
        // is already visible, the tooltip would just cover it on hover.
        const button = (
          // biome-ignore lint/correctness/useJsxKeyInIterable: key is on the <span>/<GlassTooltip> wrapper below
          <ButtonBase
            onClick={() => onNavigate(item.path)}
            onMouseEnter={() => onHoverPath?.(item.path)}
            onFocus={() => onHoverPath?.(item.path)}
            disableRipple
            aria-label={itemLabel}
            aria-current={active ? 'page' : undefined}
            sx={{
              display: 'flex',
              flexDirection: expanded ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: expanded ? 'flex-start' : 'center',
              gap: expanded ? 1.25 : 0,
              // Width tracks the rest/expanded state. Rest uses `restWidth`
              // (RAIL_REST_NARROW_WIDTH in hover-expand mode, RAIL_WIDTH
              // in legacy) — referencing a hardcoded RAIL_WIDTH here
              // overflowed the narrow strip. The 8/16px reduction matches
              // the container's horizontal margin.
              width: expanded ? `calc(${RAIL_EXPANDED_WIDTH}px - 16px)` : restWidth - 8,
              mx: expanded ? 1 : 0,
              px: expanded ? 1.25 : 0,
              // Padding + min-height stay constant across rest/expanded so
              // each row occupies exactly the same vertical space and
              // icons don't slide up/down when the rail expands. Tied to
              // py:1 (8px each side) which already gave the legacy stacked
              // layout (icon + 0.75rem caption) ~40px row height — the
              // pure-icon rest state matches that height via minHeight,
              // so cumulative top offsets across rows are identical
              // regardless of which mode is active.
              minHeight: 44,
              py: 1,
              borderRadius: 1,
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              color: active ? 'primary.main' : 'text.secondary',
              bgcolor: active && expanded ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
              transition: 'color 0.15s ease, padding 0.15s ease, gap 0.15s ease',
            }}
          >
            <Box sx={{ fontSize: 0, lineHeight: 0, flexShrink: 0 }}>{getRailIcon(item.icon)}</Box>
            {/* In expandable mode, the rest state is pure icons (Jam-e
                Jam-style narrow strip). Render the inline label only
                during the expanded state — when `expandable` is off,
                fall back to the legacy stacked icon+caption layout so
                existing portals keep their current behavior. */}
            {(expanded || !expandable) && (
              <Typography
                sx={{
                  fontSize: expanded ? '0.875rem' : '0.75rem',
                  fontWeight: active ? 600 : 500,
                  letterSpacing: '0.03125em',
                  lineHeight: 1.2,
                  mt: expanded ? 0 : 0.25,
                  flex: expanded ? 1 : 'unset',
                  maxWidth: expanded ? '100%' : RAIL_WIDTH - 12,
                  textAlign: expanded ? 'left' : 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {itemLabel}
              </Typography>
            )}
          </ButtonBase>
        );
        // Only attach a tooltip in the collapsed state — when expanded,
        // the inline label is the affordance, no need to layer a popover
        // on top.
        return expanded ? (
          <span key={item.id}>{button}</span>
        ) : (
          <GlassTooltip key={item.id} title={itemLabel} placement="top">
            <span>{button}</span>
          </GlassTooltip>
        );
      })}

      {/* Section icons — one button per section. Section ITEMS (Inbox,
          Astor, Watchlist, etc.) are NOT promoted to the rail; they
          stay in the secondary panel that opens when the user clicks a
          section. No divider between sections — the user reviewed the
          rail and asked for a clean unbroken column. */}
      {visibleSections.map((section) => {
        if (section.items.length === 0) return null;
        const sectionActive = activeSection ? section.id === activeSection : isSectionActive(section, pathname);
        const rawLabel = section.label ?? section.id;
        const sectionLabel = t(rawLabel) || rawLabel;
        // In the narrow rest state, fall back to the first word of the
        // label so multi-word section names ("Lending Operations") fit
        // under the icon. The expanded state has room for the full
        // label, so we use it as-is there.
        const compactLabel = sectionLabel.split(' ')[0] ?? sectionLabel;
        const sectionIcon = section.icon ?? 'Dashboard';
        const sectionButton = (
          // biome-ignore lint/correctness/useJsxKeyInIterable: key is on the <span>/<GlassTooltip> wrapper below
          <ButtonBase
            onClick={() => onSectionClick(section.id)}
            // Hover previews this section's panel contents without
            // committing. Mouse-out of the entire rail+panel hover region
            // (handled by PortalLayout's outer onMouseLeave) reverts to
            // the active page's section. Only fires in hoverExpand mode
            // — `onSectionHover` is undefined otherwise.
            onMouseEnter={onSectionHover ? () => onSectionHover(section.id) : undefined}
            onFocus={onSectionHover ? () => onSectionHover(section.id) : undefined}
            disableRipple
            aria-label={sectionLabel}
            aria-current={sectionActive ? 'page' : undefined}
            sx={{
              display: 'flex',
              flexDirection: expanded ? 'row' : 'column',
              alignItems: 'center',
              justifyContent: expanded ? 'flex-start' : 'center',
              gap: expanded ? 1.25 : 0,
              width: expanded ? `calc(${RAIL_EXPANDED_WIDTH}px - 16px)` : restWidth - 8,
              mx: expanded ? 1 : 0,
              px: expanded ? 1.25 : 0,
              minHeight: 44,
              py: 1,
              borderRadius: 1,
              border: 'none',
              boxShadow: 'none',
              outline: 'none',
              color: sectionActive ? 'primary.main' : 'text.secondary',
              bgcolor:
                sectionActive && expanded ? (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)') : 'transparent',
              '&:hover': {
                bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
              },
              '&:focus-visible': {
                outline: '2px solid',
                outlineColor: 'primary.main',
                outlineOffset: -2,
              },
              transition: 'color 0.15s ease',
            }}
          >
            <Box sx={{ fontSize: 0, lineHeight: 0, flexShrink: 0 }}>{getRailIcon(sectionIcon)}</Box>
            {/* In expandable mode the collapsed rail is pure icons (the
                tooltip carries the label). In legacy mode the rail
                always shows the small caption under the icon. */}
            {(expanded || !expandable) && (
              <Typography
                sx={{
                  fontSize: expanded ? '0.875rem' : '0.75rem',
                  fontWeight: sectionActive ? 600 : 500,
                  letterSpacing: '0.03125em',
                  lineHeight: 1.2,
                  mt: expanded ? 0 : 0.25,
                  flex: expanded ? 1 : 'unset',
                  maxWidth: expanded ? '100%' : RAIL_WIDTH - 12,
                  textAlign: expanded ? 'left' : 'center',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {expanded ? sectionLabel : compactLabel}
              </Typography>
            )}
          </ButtonBase>
        );
        return expanded ? (
          <span key={section.id}>{sectionButton}</span>
        ) : (
          <GlassTooltip key={section.id} title={sectionLabel} placement="top">
            <span>{sectionButton}</span>
          </GlassTooltip>
        );
      })}

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* Settings Gear — hidden when user avatar is present (settings moves into avatar menu) */}
      {config.settingsNav && onSettingsClick && !user && (
        <GlassTooltip
          title={config.settingsNav.tooltip ? t(config.settingsNav.tooltip) : t('navigation.settings')}
          placement="top"
        >
          <span>
            <ButtonBase
              onClick={onSettingsClick}
              disableRipple
              aria-label={t('navigation.settings')}
              aria-current={pathname.startsWith(config.settingsNav.path) ? 'page' : undefined}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                width: RAIL_WIDTH - 8,
                py: 0.75,
                borderRadius: 1,
                border: 'none',
                boxShadow: 'none',
                outline: 'none',
                color: pathname.startsWith(config.settingsNav.path) ? 'primary.main' : 'text.secondary',
                bgcolor: 'transparent',
                '&:hover': {
                  bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: -2,
                },
              }}
            >
              <SettingsIcon sx={{ fontSize: 22 }} /> {/* Off-scale: between base (20) and lg (24) */}
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  fontWeight: pathname.startsWith(config.settingsNav.path) ? 600 : 500,
                  letterSpacing: '0.03125em',
                  lineHeight: 1.2,
                  mt: 0.25,
                }}
              >
                {t('navigation.settings')}
              </Typography>
            </ButtonBase>
          </span>
        </GlassTooltip>
      )}

      {/* Divider before user avatar */}
      {user && <Box sx={{ width: 32, borderBottom: `1px solid ${theme.palette.divider}`, my: 0.5 }} />}

      {/* User Avatar at very bottom (Asana-style) */}
      {user && (
        <>
          <GlassTooltip title={user.name || t('common.user')} placement="top">
            <span>
              <ButtonBase
                onClick={(e) => setAvatarMenuAnchor(e.currentTarget)}
                disableRipple
                aria-label={t('a11y.openUserMenu')}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: RAIL_WIDTH - 8,
                  py: 0.75,
                  borderRadius: 1,
                  border: 'none',
                  boxShadow: 'none',
                  outline: 'none',
                  bgcolor: 'transparent',
                  '&:hover': {
                    bgcolor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: -2,
                  },
                  mb: 0.5,
                }}
              >
                <Avatar
                  src={user.avatarUrl}
                  sx={{
                    width: 32,
                    height: 32,
                    bgcolor: 'primary.main',
                    color: 'primary.contrastText',
                    fontSize: 13,
                    fontWeight: 600,
                    // Highlight ring when settings/CC section is active
                    ...(isSettingsActive && {
                      outline: '2px solid',
                      outlineColor: 'primary.main',
                      outlineOffset: 2,
                    }),
                    transition: 'outline 0.15s ease, outline-color 0.15s ease, outline-offset 0.15s ease',
                  }}
                >
                  {user.name ? computeUserInitials(user.name) : 'U'}
                </Avatar>
              </ButtonBase>
            </span>
          </GlassTooltip>

          {/* User Avatar Dropdown Menu */}
          <Menu
            anchorEl={avatarMenuAnchor}
            open={Boolean(avatarMenuAnchor)}
            onClose={() => setAvatarMenuAnchor(null)}
            transformOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{
              paper: {
                sx: {
                  minWidth: 200,
                  ml: 1,
                  '& .MuiMenuItem-root': {
                    fontSize: '0.8125rem',
                    py: 0.75,
                    minHeight: 'unset',
                  },
                  '& .MuiListItemIcon-root': {
                    minWidth: 28,
                  },
                },
              },
            }}
          >
            {/* User Info Header */}
            <Box sx={{ px: 2, py: 1 }}>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, lineHeight: 1.3 }}>
                {user.name || t('common.user')}
              </Typography>
              {user.email && (
                <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', display: 'block', lineHeight: 1.3 }}>
                  {user.email}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Theme Toggle */}
            {onThemeToggle && (
              <MenuItem
                onClick={() => {
                  onThemeToggle();
                  setAvatarMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  {isDarkMode ? (
                    <LightModeIcon sx={{ fontSize: iconSizes.md }} />
                  ) : (
                    <DarkModeIcon sx={{ fontSize: iconSizes.md }} />
                  )}
                </ListItemIcon>
                {isDarkMode ? t('theme.switchToLight') : t('theme.switchToDark')}
              </MenuItem>
            )}

            {onThemeToggle && <Divider />}

            {/* Settings / Control Center (moved from rail gear icon) */}
            {onSettingsClick && (
              <MenuItem
                onClick={() => {
                  onSettingsClick();
                  setAvatarMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <SettingsIcon sx={{ fontSize: iconSizes.md }} />
                </ListItemIcon>
                {t(settingsMenuLabel || 'navigation.settings')}
              </MenuItem>
            )}

            {onSettingsClick && <Divider />}

            {/* Personal: Profile */}
            {onProfile && (
              <MenuItem
                onClick={() => {
                  onProfile();
                  setAvatarMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <AccountCircleIcon sx={{ fontSize: iconSizes.md }} />
                </ListItemIcon>
                {t('navigation.profile')}
              </MenuItem>
            )}

            {/* Logout */}
            {onLogout && (
              <MenuItem
                onClick={() => {
                  onLogout();
                  setAvatarMenuAnchor(null);
                }}
              >
                <ListItemIcon>
                  <LogoutIcon sx={{ fontSize: iconSizes.md }} color="error" />
                </ListItemIcon>
                <Typography sx={{ fontSize: '0.8125rem' }} color="error">
                  {t('auth.logout')}
                </Typography>
              </MenuItem>
            )}
          </Menu>
        </>
      )}
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      sx={{
        // Outer width tracks the rail width directly — when the rail
        // expands (via the pin button) it PUSHES page content to the
        // right rather than overlaying it. PortalLayout's
        // `currentSidebarWidth` reads the same value so the main
        // content area's `width: calc(100% - currentSidebarWidth)`
        // shrinks in lockstep.
        width: hidden ? 0 : railWidth,
        flexShrink: 0,
        transition: 'width 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        '& .MuiDrawer-paper': {
          position: 'relative',
          width: railWidth,
          height: '100%',
          boxSizing: 'border-box',
          border: 'none',
          background: 'transparent',
          // When the secondary panel is collapsed the rail's right edge
          // becomes the divider between sidebar and main content, so it
          // needs the same hairline border + soft top-right corner the
          // panel was providing. We fade these in/out instead of
          // toggling them on `showRightEdge` so the transition matches
          // the panel's slide.
          borderRight: `0.5px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
          // Pronounced curve so the rail's top-right reads as an
          // intentional corner instead of a near-square edge. Matches
          // the SidebarPanel's top-left radius below.
          borderTopRightRadius: 16,
          // Hide the rail's right-edge chrome while the panel is open —
          // otherwise the rail's rounded top-right notches against the
          // panel's rounded top-left.
          opacity: hidden ? 0 : 1,
          ...(showRightEdge
            ? {}
            : {
                borderRightColor: 'transparent',
                borderTopRightRadius: 0,
              }),
          transform: hidden ? `translateX(-${railWidth}px)` : 'translateX(0)',
          pointerEvents: hidden ? 'none' : 'auto',
          transition:
            'width 0.25s cubic-bezier(0.4, 0, 0.2, 1), transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1), border-top-right-radius 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        },
      }}
    >
      {railContent}
    </Drawer>
  );
};

/**
 * Memoized: the rail receives mostly-stable props (config, pathname, visibilityContext,
 * callbacks). When parent contexts (Auth, Business, etc.) publish new values, this
 * bails out of re-rendering as long as the props' references are stable. Portals
 * MUST `useCallback` their `onNavigate`/`onHoverPath`/etc and `useMemo` their
 * `visibilityContext` — otherwise this memo is a no-op.
 */
export const SidebarRail = memo(SidebarRailImpl);

export default SidebarRail;
